import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  CalendarClock, Plus, Trash2, Upload, Download, Save,
  Pencil, AlertTriangle, Users, Clock, CalendarDays, Settings as SettingsIcon,
  FileText, ArrowLeft, ChevronRight, Wallet, FileSpreadsheet, Image as ImageIcon, X as XIcon,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Pager, { usePagination } from "@/components/Pager";
import { fmtCurrency, fmtHM, fmtMinsHM, getTrToday, fmtDateWithDay } from "@/utils/format";
import { useAuth } from "@/context/AuthContext";
import { MissingDocsIcon, MissingDocsBanner, PersonnelFilesSection, DeactivateDialog } from "@/components/PersonnelDocs";

const TABS = [
  { key: "employees",  label: "Personel",      icon: Users,             roles: ["admin", "sub_admin", "accounting"] },
  { key: "attendance", label: "Kayıtlar",       icon: Clock,             roles: ["admin", "sub_admin", "accounting"] },
  { key: "holidays",   label: "Resmi Tatiller", icon: CalendarDays,      roles: ["admin", "sub_admin"] },
  { key: "settings",   label: "Ayarlar",        icon: SettingsIcon,      roles: ["admin", "sub_admin"] },
];

export default function Personnel() {
  const { user } = useAuth();
  const role = user?.role;
  const visibleTabs = useMemo(() => TABS.filter((t) => t.roles.includes(role)), [role]);
  const [tab, setTab] = useState(visibleTabs[0]?.key || "employees");
  useEffect(() => {
    if (!visibleTabs.some((t) => t.key === tab)) {
      setTab(visibleTabs[0]?.key || "employees");
    }
  }, [visibleTabs, tab]);
  return (
    <div className="space-y-6" data-testid="personnel-page">
      <PageHeader kicker="İnsan Kaynakları" title="Personel & Mesai" />

      <div className="border-2 border-[#09090B] bg-white flex overflow-x-auto" data-testid="personnel-tabs">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`personnel-tab-${t.key}`}
              className={`px-5 py-3 text-sm font-mono uppercase tracking-wider flex items-center gap-2 border-r-2 border-[#09090B] last:border-r-0 ${
                active ? "bg-[#09090B] text-white" : "hover:bg-neutral-100"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "employees"  && <EmployeesTab />}
      {tab === "attendance" && <AttendanceTab />}
      {tab === "holidays"   && <HolidaysTab />}
      {tab === "settings"   && <PersonnelSettingsTab />}
    </div>
  );
}

/* ----------------------- EMPLOYEES TAB ----------------------- */
function EmployeesTab() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "sub_admin";
  const canToggleActive = ["admin", "sub_admin", "accounting"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | passive
  const [deactivating, setDeactivating] = useState(null);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    return rows.filter((r) => {
      if (statusFilter === "active" && !r.active) return false;
      if (statusFilter === "passive" && r.active) return false;
      if (!q) return true;
      const name = (r.name || "").toLocaleLowerCase("tr-TR");
      const empNo = (r.employee_no || "").toLocaleLowerCase("tr-TR");
      return name.includes(q) || empNo.includes(q);
    });
  }, [rows, search, statusFilter]);
  const { page, setPage, totalPages, paged } = usePagination(filteredRows, 10);
  useEffect(() => { setPage(1); /* reset pagination when filter changes */ }, [search, statusFilter, setPage]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/personnel/employees?_ts=${Date.now()}`);
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`"${r.name}" personelini silmek istediğinize emin misiniz?\nBu kişiye ait tüm mesai kayıtları da silinecektir.`)) return;
    try {
      await api.delete(`/personnel/employees/${r.id}`);
      toast.success("Personel silindi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActiveRow = async (r) => {
    const next = !r.active;
    if (!next) { setDeactivating(r); return; }
    if (!window.confirm(`"${r.name}" Aktif yapılsın mı?`)) return;
    try {
      await api.patch(`/personnel/employees/${r.id}/active`, { active: next });
      toast.success(next ? "Aktif yapıldı" : "Pasif yapıldı");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-neutral-500" data-testid="personnel-count">
          Toplam {filteredRows.length} personel
          {(search || statusFilter !== "all") && rows.length !== filteredRows.length && (
            <span className="text-neutral-400 ml-1">/ {rows.length} kayıt</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]"
            onClick={async () => {
              try {
                const res = await api.get("/personnel/employees/export-xlsx", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = "personel_listesi.xlsx"; a.click();
                URL.revokeObjectURL(url);
              } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
            }}
            data-testid="personnel-export-btn">
            <Download size={14} className="mr-2" /> Excel İndir
          </Button>
          <Button variant="outline" className="rounded-none border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white"
            onClick={() => setBulkOpen(true)} data-testid="personnel-bulk-btn">
            <Upload size={14} className="mr-2" /> Toplu İçe Aktar
          </Button>
          <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={() => { setEditing(null); setOpen(true); }} data-testid="personnel-new-btn">
            <Plus size={16} className="mr-2" /> Yeni Personel
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px] max-w-md">
          <Label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Ara</Label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ad Soyad veya Sicil No…"
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-9"
            data-testid="personnel-search"
          />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Durum</Label>
          <div className="flex items-stretch border-2 border-[#09090B] h-9" data-testid="personnel-status-filter">
            {[
              { key: "all",     label: "Tümü" },
              { key: "active",  label: "Aktif" },
              { key: "passive", label: "Pasif" },
            ].map((opt, idx) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setStatusFilter(opt.key)}
                className={`px-3 text-xs font-mono uppercase transition-colors ${
                  idx > 0 ? "border-l-2 border-[#09090B]" : ""
                } ${
                  statusFilter === opt.key
                    ? "bg-[#09090B] text-white"
                    : "bg-white text-neutral-700 hover:bg-neutral-100"
                }`}
                data-testid={`personnel-status-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {(search || statusFilter !== "all") && (
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B] h-9 text-xs"
            onClick={() => { setSearch(""); setStatusFilter("all"); }}
            data-testid="personnel-clear-filters"
          >
            Filtreleri Temizle
          </Button>
        )}
      </div>

      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs w-14"></TableHead>
              <TableHead className="font-mono uppercase text-xs">Sicil No</TableHead>
              <TableHead className="font-mono uppercase text-xs">Ad Soyad</TableHead>
              <TableHead className="font-mono uppercase text-xs">Pozisyon</TableHead>
              <TableHead className="font-mono uppercase text-xs">İşe Giriş</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">Saatlik / Aylık</TableHead>
              <TableHead className="font-mono uppercase text-xs text-center">Durum</TableHead>
              <TableHead className="font-mono uppercase text-xs">Notlar</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10 text-neutral-500 italic">
                  {loading ? "Yükleniyor…" : "Kayıt yok"}
                </TableCell>
              </TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10 text-neutral-500 italic" data-testid="personnel-empty-filter">
                  Filtreye uyan kayıt bulunamadı
                </TableCell>
              </TableRow>
            ) : paged.map((r) => (
              <TableRow key={r.id} data-testid={`personnel-row-${r.id}`}
                className="cursor-pointer hover:bg-neutral-50"
                onClick={() => setViewing(r)}>
                <TableCell className="py-2">
                  {r.photo_b64 ? (
                    <img src={r.photo_b64} alt={r.name}
                      className="w-9 h-9 rounded-full object-cover border-2 border-[#09090B]"
                      data-testid={`personnel-avatar-${r.id}`} />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-neutral-200 border-2 border-[#09090B] flex items-center justify-center text-[10px] font-mono uppercase font-bold text-neutral-600"
                      data-testid={`personnel-avatar-${r.id}`}>
                      {(r.name || "?").trim().split(/\s+/).map(s => s[0] || "").slice(0, 2).join("")}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">{r.employee_no || "—"}</TableCell>
                <TableCell className="font-semibold">{r.name}<MissingDocsIcon person={r} /></TableCell>
                <TableCell className="text-xs text-neutral-600">{r.position || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.hire_date || "—"}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {r.hourly_wage ? (
                    <span className="text-[#0055FF] font-bold">{fmtCurrency(r.hourly_wage)}/sa</span>
                  ) : r.monthly_salary ? (
                    <span className="text-emerald-700 font-bold">{fmtCurrency(r.monthly_salary)}/ay</span>
                  ) : (
                    <span className="text-neutral-400">varsayılan</span>
                  )}
                </TableCell>
                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                  {canToggleActive ? (
                    <button
                      type="button"
                      onClick={() => toggleActiveRow(r)}
                      title={r.active ? "Pasife almak için tıkla" : "Aktife almak için tıkla"}
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 border-2 text-[10px] font-mono uppercase transition-colors ${r.active ? "border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white" : "border-neutral-500 text-neutral-500 hover:bg-neutral-500 hover:text-white"}`}
                      data-testid={`personnel-status-toggle-${r.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${r.active ? "bg-emerald-600" : "bg-neutral-400"}`}></span>
                      {r.active ? "Aktif" : "Pasif"}
                    </button>
                  ) : (
                    <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${r.active ? "border-emerald-600 text-emerald-700" : "border-neutral-400 text-neutral-500"}`}>
                      {r.active ? "Aktif" : "Pasif"}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-neutral-500 max-w-[240px] truncate">{r.notes || ""}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-2 justify-end">
                    {(canEdit || user?.role === "accounting") && (
                      <Button size="icon" variant="outline" className="rounded-none border-2 border-[#09090B] h-8 w-8"
                        onClick={() => { setEditing(r); setOpen(true); }} data-testid={`personnel-edit-${r.id}`}>
                        <Pencil size={14} />
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Button size="icon" variant="outline" className="rounded-none border-2 border-red-600 text-red-600 h-8 w-8 hover:bg-red-600 hover:text-white"
                          onClick={() => remove(r)} data-testid={`personnel-del-${r.id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {open && <EmployeeDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={() => { setOpen(false); load(); }} />}
      {viewing && <PersonnelDetailDialog person={viewing} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); setOpen(true); }} onChanged={load} />}
      {deactivating && <DeactivateDialog person={deactivating} onClose={() => setDeactivating(null)} onDone={() => { setDeactivating(null); load(); }} />}
      {bulkOpen && <BulkImportPersonnelDialog onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
      <Pager page={page} setPage={setPage} totalPages={totalPages} testid="personnel-pager" />
    </div>
  );
}

function BulkImportPersonnelDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/personnel/employees/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "personel_sablon.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doImport = async () => {
    if (!file) { toast.error("Excel dosyası seçin"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/personnel/employees/bulk-import-xlsx", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.inserted} eklendi · ${data.skipped} atlandı`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none border-2 border-[#09090B] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Toplu Personel İçe Aktarma</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-neutral-600 leading-relaxed">
            Şablon sütunları: <b>Sicil No · Ad Soyad · Pozisyon · Maaş · Saatlik Ücret · Giriş Saati · Çıkış Saati</b>.
            Ad Soyad zorunludur. Aynı Sicil No veya Ad Soyad'lı personel varsa satır atlanır.
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={downloadTemplate} data-testid="personnel-bulk-template">
              <Download size={14} className="mr-2" /> Örnek Şablon
            </Button>
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs font-mono uppercase">Excel Dosyası (.xlsx)</Label>
              <input type="file" accept=".xlsx,.xls"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                className="block w-full text-sm border-2 border-[#09090B] p-2 rounded-none"
                data-testid="personnel-bulk-file" />
            </div>
          </div>
          {result && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div className="border-2 border-emerald-600 p-2 text-emerald-800">
                  <div className="font-mono uppercase text-[10px] tracking-widest">Eklenen</div>
                  <div className="text-xl font-black">{result.inserted}</div>
                </div>
                <div className="border-2 border-yellow-500 p-2 text-yellow-800">
                  <div className="font-mono uppercase text-[10px] tracking-widest">Atlanan</div>
                  <div className="text-xl font-black">{result.skipped}</div>
                </div>
              </div>
              {result.skipped_rows?.length > 0 && (
                <div className="border-2 border-yellow-500 bg-yellow-50 p-2 max-h-40 overflow-y-auto" data-testid="personnel-bulk-skipped">
                  <div className="font-bold flex items-center gap-1 text-yellow-800 mb-1">
                    <AlertTriangle size={12} /> Atlanan Satırlar
                  </div>
                  <ul className="space-y-0.5">
                    {result.skipped_rows.map((s, i) => (
                      <li key={i}>Satır {s.row}: {s.name} — {s.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onClose}>Kapat</Button>
          {!result ? (
            <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]"
              onClick={doImport} disabled={busy || !file} data-testid="personnel-bulk-submit">
              <Upload size={14} className="mr-2" /> {busy ? "Yükleniyor…" : "İçe Aktar"}
            </Button>
          ) : (
            <Button className="rounded-none bg-emerald-700 hover:bg-emerald-800" onClick={onDone} data-testid="personnel-bulk-done">
              Tamam
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------- PERSONEL DETAY DIALOG ----------------------- */
async function _resizePhotoToDataURL(file, maxDim = 400, quality = 0.85) {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

function PersonnelLeaveSection({ pid, canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", leave_type: "yillik", note: "" });
  const [pdfFile, setPdfFile] = useState(null);
  const pdfRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/personnel/employees/${pid}/leave-summary`);
      setData(d);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/personnel/leave-types");
        setTypes(d?.types || []);
      } catch { /* noop */ }
    })();
  }, []);

  const resetForm = () => {
    setForm({ start_date: "", end_date: "", leave_type: "yillik", note: "" });
    setPdfFile(null);
    if (pdfRef.current) pdfRef.current.value = "";
  };

  const save = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Başlangıç ve bitiş tarihi zorunlu"); return; }
    if (!pdfFile) { toast.error("İzin belgesi (PDF) zorunludur"); return; }
    if (!pdfFile.name.toLowerCase().endsWith(".pdf")) { toast.error("Sadece PDF"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("personnel_id", pid);
      fd.append("start_date", form.start_date);
      fd.append("end_date", form.end_date);
      fd.append("leave_type", form.leave_type);
      fd.append("note", form.note || "");
      fd.append("file", pdfFile);
      await api.post("/personnel/leaves", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("İzin eklendi");
      setAddOpen(false);
      resetForm();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (lid) => {
    if (!window.confirm("Bu izin kaydı silinsin mi?")) return;
    try { await api.delete(`/personnel/leaves/${lid}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openLeavePdf = (lid) => {
    window.open(`${API}/api/personnel/leaves/${lid}/download`, "_blank", "noopener,noreferrer");
  };

  const remaining = data ? (data.total_entitled - data.used) : 0;
  const remainTone = remaining < 0 ? "text-red-700" : remaining <= 3 ? "text-amber-700" : "text-emerald-700";
  const typeMeta = (v) => types.find((t) => t.value === v) || {};

  return (
    <div data-testid="detail-leave-section">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">İzinler</div>
        {canEdit && (
          <Button size="sm" variant="outline" className="rounded-none border-2 border-[#09090B] h-7 text-[11px]"
            onClick={() => setAddOpen((v) => !v)} data-testid="detail-leave-add-toggle">
            {addOpen ? "İptal" : "+ İzin Ekle"}
          </Button>
        )}
      </div>
      {loading ? (
        <div className="text-xs text-neutral-400 italic py-2">Yükleniyor…</div>
      ) : data ? (
        <>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div className="border-2 border-[#09090B] p-2 bg-white">
              <div className="text-[9px] font-mono uppercase text-neutral-500">Toplam Hak</div>
              <div className="text-lg font-black" data-testid="leave-total">{data.total_entitled} <span className="text-[10px] font-mono text-neutral-500">gün</span></div>
            </div>
            <div className="border-2 border-[#09090B] p-2 bg-white">
              <div className="text-[9px] font-mono uppercase text-neutral-500">Kullanılan</div>
              <div className="text-lg font-black" data-testid="leave-used">{data.used} <span className="text-[10px] font-mono text-neutral-500">gün</span></div>
            </div>
            <div className="border-2 border-[#09090B] p-2 bg-white">
              <div className="text-[9px] font-mono uppercase text-neutral-500">Kalan</div>
              <div className={`text-lg font-black ${remainTone}`} data-testid="leave-remaining">{remaining} <span className="text-[10px] font-mono text-neutral-500">gün</span></div>
            </div>
          </div>
          {data.notes && data.notes.length > 0 && (
            <div className="text-[10px] text-amber-700 mb-2">{data.notes.join(" · ")}</div>
          )}
          {addOpen && canEdit && (
            <div className="border-2 border-[#0055FF] bg-blue-50 p-2 mb-2 space-y-2" data-testid="detail-leave-add-form">
              <div>
                <Label className="text-[10px] font-mono uppercase">İzin Türü</Label>
                <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                  className="w-full rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-8 text-sm px-2 bg-white"
                  data-testid="leave-type">
                  {types.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label} {t.deduct_entitlement ? " · hak edişten düşer" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-mono uppercase">Başlangıç</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-8" data-testid="leave-start" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase">Bitiş</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-8" data-testid="leave-end" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase">Açıklama</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-8" placeholder="opsiyonel" data-testid="leave-note" />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase">İzin Belgesi (PDF) <span className="text-red-600">*</span></Label>
                <input ref={pdfRef} type="file" accept="application/pdf,.pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  className="w-full text-xs border-2 border-[#09090B] bg-white h-8 px-2 py-1 file:mr-2 file:bg-[#09090B] file:text-white file:border-0 file:px-2 file:py-1 file:text-[10px] file:font-mono file:uppercase"
                  data-testid="leave-pdf-input" />
                <div className="text-[10px] text-neutral-500 mt-1">Zorunlu · Max 10 MB · sadece .pdf {pdfFile ? `· ${pdfFile.name}` : ""}</div>
              </div>
              <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B] h-8" onClick={save} disabled={saving || !pdfFile} data-testid="leave-save">
                {saving ? "Kaydediliyor…" : "Kaydet"}
              </Button>
            </div>
          )}
          {data.entries && data.entries.length > 0 ? (
            <div className="border border-neutral-200 max-h-56 overflow-y-auto divide-y divide-neutral-100">
              {data.entries.map((e) => {
                const meta = typeMeta(e.leave_type);
                const deductBadge = meta.deduct_entitlement
                  ? <span className="inline-block px-1.5 py-0.5 border border-amber-600 text-amber-800 text-[9px] font-mono uppercase">Hak edişten düşer</span>
                  : <span className="inline-block px-1.5 py-0.5 border border-emerald-600 text-emerald-700 text-[9px] font-mono uppercase">Ücretli</span>;
                return (
                  <div key={e.id} className="flex items-center gap-2 px-2 py-1 text-xs" data-testid={`leave-row-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-block px-1.5 py-0.5 bg-[#09090B] text-white text-[9px] font-mono uppercase">{e.leave_type_label || e.leave_type}</span>
                        {deductBadge}
                        <span className="font-mono text-neutral-700">{e.start_date} → {e.end_date}</span>
                        <span className="text-neutral-500">({e.days} gün)</span>
                        {e.has_pdf && (
                          <button onClick={() => openLeavePdf(e.id)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white text-[9px] font-mono uppercase"
                            data-testid={`leave-pdf-${e.id}`} title="Belgeyi aç">
                            <FileText size={10} /> PDF
                          </button>
                        )}
                      </div>
                      {e.note && <div className="text-[10px] text-neutral-500 truncate mt-0.5">{e.note}</div>}
                    </div>
                    {canEdit && (
                      <button onClick={() => remove(e.id)} className="text-red-600 hover:text-red-800 p-1"
                        title="Sil" data-testid={`leave-del-${e.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[11px] text-neutral-400 italic py-1">Henüz izin kullanılmamış</div>
          )}
          {data.years && data.years.length > 0 && (
            <details className="mt-2 text-[11px]">
              <summary className="cursor-pointer text-neutral-500 hover:text-neutral-900">Yıl bazında döküm ({data.years.length} yıl)</summary>
              <div className="mt-1 border border-neutral-200 divide-y divide-neutral-100 font-mono">
                {data.years.map((y) => (
                  <div key={y.year} className="flex justify-between px-2 py-0.5">
                    <span>{y.year}. yıl · {y.anniversary}{y.age >= 0 ? ` · ${y.age} yaş` : ""}</span>
                    <span className="font-bold">{y.days} gün</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      ) : null}
    </div>
  );
}

function PersonnelContractsSection({ pid }) {
  const { user } = useAuth();
  const canUpload = ["admin", "sub_admin", "accounting"].includes(user?.role);
  const canDelete = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [desc, setDesc] = useState("");
  const fileRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/personnel/contracts?personnel_id=${pid}&_ts=${Date.now()}`);
      setRows(Array.isArray(data) ? data : []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const doUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("Sadece PDF"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("personnel_id", pid);
    fd.append("description", desc || "");
    fd.append("file", f);
    try {
      await api.post("/personnel/contracts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sözleşme yüklendi");
      setDesc("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (cid) => {
    if (!window.confirm("Bu sözleşme silinsin mi?")) return;
    try { await api.delete(`/personnel/contracts/${cid}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openContract = (cid) => {
    window.open(`${API}/api/personnel/contracts/${cid}/download`, "_blank", "noopener,noreferrer");
  };

  const fmtSize = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };
  const fmtAt = (iso) => { try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; } };

  return (
    <div data-testid="detail-contracts-section" className="space-y-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Sözleşmeler</div>
      {canUpload && (
        <div className="border-2 border-[#0055FF] bg-blue-50 p-2 space-y-2" data-testid="contracts-upload-form">
          <div>
            <Label className="text-[10px] font-mono uppercase">Açıklama</Label>
            <Input value={desc} onChange={(e) => setDesc(e.target.value)}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-8"
              placeholder="opsiyonel — örn: İş sözleşmesi 2026" data-testid="contract-desc" />
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
            onChange={doUpload} data-testid="contract-file-input" />
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B] h-8"
            onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="contract-upload-btn">
            <Upload size={14} className="mr-2" /> {uploading ? "Yükleniyor…" : "PDF Yükle"}
          </Button>
          <div className="text-[10px] text-neutral-500">Max 10 MB · sadece .pdf</div>
        </div>
      )}
      {loading ? (
        <div className="text-xs text-neutral-400 italic py-2">Yükleniyor…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-neutral-400 italic py-2">Henüz sözleşme yok</div>
      ) : (
        <div className="border border-neutral-200 max-h-72 overflow-y-auto divide-y divide-neutral-100">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid={`contract-row-${r.id}`}>
              <button onClick={() => openContract(r.id)}
                className="flex-1 min-w-0 text-left hover:text-[#0055FF]" data-testid={`contract-open-${r.id}`}>
                <div className="font-mono text-neutral-900 truncate">{r.filename}</div>
                <div className="text-[10px] text-neutral-500 truncate">
                  {r.description || "—"} · {fmtSize(r.size)} · {fmtAt(r.uploaded_at)} · {r.uploaded_by}
                </div>
              </button>
              {canDelete && (
                <button onClick={() => remove(r.id)} className="text-red-600 hover:text-red-800 p-1"
                  title="Sil (yalnız yönetici)" data-testid={`contract-del-${r.id}`}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PersonnelMinutesSection({ pid }) {
  const { user } = useAuth();
  const canUpload = ["admin", "sub_admin", "accounting"].includes(user?.role);
  const canDelete = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [desc, setDesc] = useState("");
  const fileRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/personnel/minutes?personnel_id=${pid}&_ts=${Date.now()}`);
      setRows(Array.isArray(data) ? data : []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pid]);

  const doUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("Sadece PDF"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("personnel_id", pid);
    fd.append("description", desc || "");
    fd.append("file", f);
    try {
      await api.post("/personnel/minutes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Tutanak yüklendi");
      setDesc("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (mid) => {
    if (!window.confirm("Bu tutanak silinsin mi?")) return;
    try { await api.delete(`/personnel/minutes/${mid}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openMinute = (mid) => {
    window.open(`${API}/api/personnel/minutes/${mid}/download`, "_blank", "noopener,noreferrer");
  };

  const fmtSize = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };
  const fmtAt = (iso) => { try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; } };

  return (
    <div data-testid="detail-minutes-section" className="space-y-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Tutanaklar</div>
      {canUpload && (
        <div className="border-2 border-[#0055FF] bg-blue-50 p-2 space-y-2" data-testid="minutes-upload-form">
          <div>
            <Label className="text-[10px] font-mono uppercase">Açıklama</Label>
            <Input value={desc} onChange={(e) => setDesc(e.target.value)}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-8"
              placeholder="opsiyonel — örn: Uyarı tutanağı 2026" data-testid="minute-desc" />
          </div>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden"
            onChange={doUpload} data-testid="minute-file-input" />
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B] h-8"
            onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="minute-upload-btn">
            <Upload size={14} className="mr-2" /> {uploading ? "Yükleniyor…" : "PDF Yükle"}
          </Button>
          <div className="text-[10px] text-neutral-500">Max 10 MB · sadece .pdf</div>
        </div>
      )}
      {loading ? (
        <div className="text-xs text-neutral-400 italic py-2">Yükleniyor…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-neutral-400 italic py-2">Henüz tutanak yok</div>
      ) : (
        <div className="border border-neutral-200 max-h-72 overflow-y-auto divide-y divide-neutral-100">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid={`minute-row-${r.id}`}>
              <button onClick={() => openMinute(r.id)}
                className="flex-1 min-w-0 text-left hover:text-[#0055FF]" data-testid={`minute-open-${r.id}`}>
                <div className="font-mono text-neutral-900 truncate">{r.filename}</div>
                <div className="text-[10px] text-neutral-500 truncate">
                  {r.description || "—"} · {fmtSize(r.size)} · {fmtAt(r.uploaded_at)} · {r.uploaded_by}
                </div>
              </button>
              {canDelete && (
                <button onClick={() => remove(r.id)} className="text-red-600 hover:text-red-800 p-1"
                  title="Sil (yalnız yönetici)" data-testid={`minute-del-${r.id}`}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PersonnelDetailDialog({ person, onClose, onEdit, onChanged }) {
  const [p, setP] = useState(person || {});
  const [uploading, setUploading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [tab, setTab] = useState("personal");
  const [deactivating, setDeactivating] = useState(false);
  const fileRef = useRef(null);
  const { user } = useAuth();
  const canToggleActive = ["admin", "sub_admin", "accounting"].includes(user?.role);
  const canEditProfile = ["admin", "sub_admin", "accounting"].includes(user?.role);

  useEffect(() => { setP(person || {}); }, [person]);

  const loadHistory = async (pid) => {
    if (!pid) return;
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/personnel/employees/${pid}/history`);
      setHistory(data?.items || []);
    } catch (err) { /* sessizce yut */ }
    finally { setLoadingHistory(false); }
  };
  useEffect(() => { if (person?.id) loadHistory(person.id); else setHistory([]); }, [person?.id]);

  const toggleActive = async () => {
    if (toggling) return;
    const next = !p.active;
    if (!next) { setDeactivating(true); return; }
    if (!window.confirm("Personel Aktif yapılsın mı?")) return;
    setToggling(true);
    try {
      await api.patch(`/personnel/employees/${p.id}/active`, { active: next });
      setP({ ...p, active: next });
      toast.success(next ? "Aktif yapıldı" : "Pasif yapıldı");
      onChanged && onChanged();
      loadHistory(p.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setToggling(false); }
  };

  const fmtAuditAt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("tr-TR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Fotoğraf çok büyük (max 5 MB)"); return; }
    setUploading(true);
    try {
      const dataUrl = await _resizePhotoToDataURL(file);
      await api.patch(`/personnel/employees/${p.id}/photo`, { photo_b64: dataUrl });
      setP({ ...p, photo_b64: dataUrl });
      toast.success("Fotoğraf güncellendi");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Yükleme başarısız"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = async () => {
    if (!window.confirm("Fotoğraf silinsin mi?")) return;
    try {
      await api.patch(`/personnel/employees/${p.id}/photo`, { photo_b64: null });
      setP({ ...p, photo_b64: null });
      toast.success("Fotoğraf silindi");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const Row = ({ label, value, testid, mono = false }) => (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-neutral-100">
      <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 pt-0.5">{label}</div>
      <div className={`col-span-2 text-sm ${mono ? "font-mono" : ""} ${value ? "text-neutral-900" : "text-neutral-400 italic"}`} data-testid={testid}>
        {value || "—"}
      </div>
    </div>
  );
  const wageDisplay = p.hourly_wage
    ? `${fmtCurrency(p.hourly_wage)} / saat`
    : p.monthly_salary
    ? `${fmtCurrency(p.monthly_salary)} / ay`
    : "Ayarlar varsayılanı";
  return (
    <Dialog open={!!person} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-[#09090B] max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2 pr-8">
            <span>{p.name}</span>
            {canToggleActive ? (
              <button
                type="button"
                onClick={toggleActive}
                disabled={toggling}
                title={p.active ? "Pasife almak için tıkla" : "Aktife almak için tıkla"}
                className={`inline-flex items-center gap-2 px-2 py-0.5 border-2 text-[10px] font-mono uppercase transition-colors disabled:opacity-50 ${p.active ? "border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white" : "border-neutral-500 text-neutral-500 hover:bg-neutral-500 hover:text-white"}`}
                data-testid="detail-active-toggle">
                <span className={`w-2 h-2 rounded-full ${p.active ? "bg-emerald-600" : "bg-neutral-400"}`}></span>
                {p.active ? "Aktif" : "Pasif"}
              </button>
            ) : (
              <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono uppercase ${p.active ? "border-emerald-600 text-emerald-700" : "border-neutral-400 text-neutral-500"}`}>
                {p.active ? "Aktif" : "Pasif"}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Fotoğraf alanı */}
        <div className="flex items-start gap-4 pt-2">
          <div className="w-24 h-24 border-2 border-[#09090B] bg-neutral-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="detail-photo-slot">
            {p.photo_b64 ? (
              <img src={p.photo_b64} alt={p.name} className="w-full h-full object-cover" data-testid="detail-photo-img" />
            ) : (
              <div className="text-[10px] font-mono text-neutral-400 text-center px-1">Fotoğraf<br/>yok</div>
            )}
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            {canEditProfile && (
              <>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="detail-photo-input" />
                <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="detail-photo-upload">
                  <Upload size={14} className="mr-2" /> {p.photo_b64 ? "Değiştir" : "Fotoğraf Yükle"}
                </Button>
                {p.photo_b64 && (
                  <Button variant="outline" className="rounded-none border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                    onClick={removePhoto} data-testid="detail-photo-remove">
                    <Trash2 size={14} className="mr-2" /> Kaldır
                  </Button>
                )}
                <div className="text-[10px] text-neutral-500 basis-full">JPG/PNG · Otomatik 400px'e küçültülür.</div>
              </>
            )}
            {!canEditProfile && (
              <div className="text-[10px] text-neutral-500">Fotoğrafı yalnızca yönetici değiştirebilir.</div>
            )}
          </div>
        </div>

        {deactivating && (
          <DeactivateDialog person={p} onClose={() => setDeactivating(false)}
            onDone={() => { setDeactivating(false); setP({ ...p, active: false }); onChanged && onChanged(); loadHistory(p.id); }} />
        )}
        <MissingDocsBanner person={p} onClick={() => setTab("documents")} />

        <div className="space-y-4 pt-2">
          {/* Sekmeler */}
          {(() => {
            const TABS = [
              { key: "personal", label: "Kişisel Bilgiler" },
              { key: "work", label: "İş Bilgileri" },
              { key: "salary", label: "Maaş" },
              { key: "leaves", label: "İzinler" },
              { key: "documents", label: "Özlük Belgeleri" },
              { key: "minutes", label: "Tutanaklar" },
              { key: "emergency", label: "Acil İletişim" },
              { key: "history", label: "Geçmiş" },
            ];
            return (
              <div className="flex flex-wrap gap-1 border-b-2 border-[#09090B]" data-testid="detail-tabs">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`px-3 py-1.5 text-xs font-mono uppercase border-2 border-b-0 -mb-[2px] transition-colors ${
                      tab === t.key
                        ? "border-[#09090B] bg-[#09090B] text-white"
                        : "border-transparent text-neutral-500 hover:text-neutral-900"
                    }`}
                    data-testid={`detail-tab-${t.key}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            );
          })()}

          {tab === "personal" && (
            <div data-testid="tab-personal">
              <Row label="Ad Soyad" value={p.name} testid="detail-name" />
              <Row label="Doğum Tarihi" value={p.birth_date} testid="detail-birth-date" mono />
              <Row label="Kimlik No" value={p.id_no} testid="detail-id-no" mono />
              <Row label="Cinsiyet" value={p.gender} testid="detail-gender" />
              <Row label="Medeni Hal" value={p.marital_status} testid="detail-marital" />
              <Row label="Uyruk" value={p.nationality} testid="detail-nationality" />
              <Row label="Kan Grubu" value={p.blood_type} testid="detail-blood" mono />
              <Row label="Adres" value={p.address} testid="detail-address" />
              <Row label="Cep Telefonu" value={p.mobile_phone} testid="detail-mobile" mono />
              {p.notes && (
                <div className="mt-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Notlar</div>
                  <div className="text-sm bg-neutral-50 p-3 border border-neutral-200 whitespace-pre-wrap" data-testid="detail-notes">{p.notes}</div>
                </div>
              )}
            </div>
          )}

          {tab === "work" && (
            <div data-testid="tab-work">
              <Row label="İşe Giriş" value={p.hire_date} testid="detail-hire-date" mono />
              <Row label="Sicil No" value={p.employee_no} testid="detail-employee-no" mono />
              <Row label="Pozisyon" value={p.position} testid="detail-position" />
              <Row label="Askerlik" value={p.military_status} testid="detail-military" />
              <Row label="Ehliyet" value={p.driving_license} testid="detail-license" mono />
              <Row label="Durum" value={p.active ? "Aktif" : "Pasif"} testid="detail-status" />
              <div className="mt-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Çalışma Saatleri</div>
                <Row label="Pzt-Cum" value={p.work_start && p.work_end ? `${p.work_start} – ${p.work_end}` : ""} mono />
                <Row label="Cumartesi" value={p.work_start_sat && p.work_end_sat ? `${p.work_start_sat} – ${p.work_end_sat}` : ""} mono />
              </div>
            </div>
          )}

          {tab === "salary" && (
            <div data-testid="tab-salary" className="space-y-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Ücret</div>
                <Row label="Aylık Maaş" value={p.monthly_salary ? fmtCurrency(p.monthly_salary) : ""} testid="detail-monthly" mono />
                <Row label="Saatlik Ücret" value={p.hourly_wage ? `${fmtCurrency(p.hourly_wage)} / saat` : ""} testid="detail-hourly" mono />
                <Row label="Ücret Kaynağı" value={wageDisplay} testid="detail-wage" mono />
              </div>
            </div>
          )}

          {tab === "leaves" && (
            <div data-testid="tab-leaves">
              <PersonnelLeaveSection pid={p.id} canEdit={canEditProfile} />
            </div>
          )}

          {tab === "documents" && (
            <div data-testid="tab-documents">
              <PersonnelFilesSection pid={p.id} onMissingChange={(missing) => { setP((prev) => ({ ...prev, missing_docs: missing })); onChanged && onChanged(); }} />
            </div>
          )}

          {tab === "minutes" && (
            <div data-testid="tab-minutes">
              <PersonnelMinutesSection pid={p.id} />
            </div>
          )}

          {tab === "emergency" && (
            <div data-testid="tab-emergency">
              <Row label="Ad Soyad" value={p.emergency_contact_name} testid="detail-emc-name" />
              <Row label="Yakınlık" value={p.emergency_contact_relation} testid="detail-emc-relation" />
              <Row label="Cep Telefonu" value={p.emergency_contact_phone} testid="detail-emc-phone" mono />
            </div>
          )}

          {tab === "advances" && null}

          {tab === "history" && (
            <div data-testid="tab-history">
              <div data-testid="detail-audit-section">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Değişiklik Geçmişi</div>
                {loadingHistory ? (
                  <div className="text-xs text-neutral-400 italic py-2">Yükleniyor…</div>
                ) : history.length === 0 ? (
                  <div className="text-xs text-neutral-400 italic py-2" data-testid="detail-audit-empty">Henüz kayıt yok</div>
                ) : (
                  <div className="border border-neutral-200 max-h-72 overflow-y-auto divide-y divide-neutral-100">
                    {history.map((h) => (
                      <div key={h.id} className="flex items-start gap-2 px-2 py-1.5 text-xs" data-testid={`detail-audit-row-${h.id}`}>
                        <span className={`shrink-0 mt-0.5 inline-block w-1.5 h-1.5 rounded-full ${h.new === "Aktif" ? "bg-emerald-600" : "bg-neutral-400"}`}></span>
                        <div className="flex-1 min-w-0">
                          <div className="text-neutral-900">
                            <span className="font-mono uppercase text-[10px] text-neutral-500 mr-1">{h.label || h.field}</span>
                            <span className={`font-mono ${h.old === "Aktif" ? "text-emerald-700" : "text-neutral-500"}`}>{h.old || "—"}</span>
                            <span className="mx-1 text-neutral-400">→</span>
                            <span className={`font-mono font-bold ${h.new === "Aktif" ? "text-emerald-700" : "text-neutral-600"}`}>{h.new || "—"}</span>
                          </div>
                          <div className="text-[10px] text-neutral-500 mt-0.5 truncate">
                            {h.actor_name || h.actor_email || "-"} · <span className="font-mono">{fmtAuditAt(h.at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onClose} data-testid="detail-close">Kapat</Button>
          {canEditProfile && (
            <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={onEdit} data-testid="detail-edit">
              <Pencil size={14} className="mr-2" /> Düzenle
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmployeeDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(editing || { name: "", employee_no: "", active: true, notes: "", hire_date: getTrToday(), hourly_wage: "", monthly_salary: "", position: "", work_start: "", work_end: "", work_start_sat: "", work_end_sat: "", address: "", mobile_phone: "", blood_type: "", emergency_contact_name: "", emergency_contact_relation: "", emergency_contact_phone: "", birth_date: "", id_no: "", marital_status: "", nationality: "", military_status: "", gender: "", driving_license: "", photo_b64: null });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Fotoğraf çok büyük (max 5 MB)"); return; }
    setUploadingPhoto(true);
    try {
      const dataUrl = await _resizePhotoToDataURL(file);
      setForm((f) => ({ ...f, photo_b64: dataUrl }));
    } catch (err) { toast.error("Görsel okunamadı"); }
    finally { setUploadingPhoto(false); if (photoInputRef.current) photoInputRef.current.value = ""; }
  };
  const clearPhoto = () => setForm((f) => ({ ...f, photo_b64: null }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Ad zorunlu"); return; }
    if (!form.hire_date) { toast.error("İşe giriş tarihi zorunlu"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        hire_date: form.hire_date,
        hourly_wage: form.hourly_wage === "" || form.hourly_wage === null ? null : Number(form.hourly_wage),
        monthly_salary: form.monthly_salary === "" || form.monthly_salary === null ? null : Number(form.monthly_salary),
        work_start: form.work_start || null,
        work_end: form.work_end || null,
        work_start_sat: form.work_start_sat || null,
        work_end_sat: form.work_end_sat || null,
        photo_b64: form.photo_b64 || null,
      };
      if (editing) await api.put(`/personnel/employees/${editing.id}`, payload);
      else await api.post("/personnel/employees", payload);
      toast.success(editing ? "Personel güncellendi" : "Personel eklendi");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-[#09090B] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Personel Düzenle" : "Yeni Personel"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {/* Profil Fotoğrafı */}
          <div className="flex items-start gap-3 p-3 border-2 border-neutral-200 bg-neutral-50">
            <div className="w-20 h-20 border-2 border-[#09090B] bg-white flex items-center justify-center overflow-hidden shrink-0" data-testid="personnel-form-photo-slot">
              {form.photo_b64 ? (
                <img src={form.photo_b64} alt="preview" className="w-full h-full object-cover" data-testid="personnel-form-photo-preview" />
              ) : (
                <div className="text-[10px] font-mono text-neutral-400 text-center px-1">Fotoğraf<br/>yok</div>
              )}
            </div>
            <div className="flex-1 flex flex-wrap gap-2 items-start">
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} data-testid="personnel-form-photo-input" />
              <Button type="button" variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto} data-testid="personnel-form-photo-upload">
                <Upload size={14} className="mr-2" /> {form.photo_b64 ? "Değiştir" : "Fotoğraf Yükle"}
              </Button>
              {form.photo_b64 && (
                <Button type="button" variant="outline" className="rounded-none border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                  onClick={clearPhoto} data-testid="personnel-form-photo-remove">
                  <Trash2 size={14} className="mr-2" /> Kaldır
                </Button>
              )}
              <div className="text-[10px] text-neutral-500 basis-full">Opsiyonel · JPG/PNG · 400px'e otomatik küçültülür.</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-mono uppercase">Ad Soyad *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-name" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Sicil No</Label>
              <Input value={form.employee_no || ""} onChange={(e) => setForm({ ...form, employee_no: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-no" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Pozisyon</Label>
            <Input value={form.position || ""} onChange={(e) => setForm({ ...form, position: e.target.value })}
              placeholder="Örn: Usta, Muhasebe, Ofis"
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-position" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">İşe Giriş Tarihi *</Label>
            <Input type="date" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-hire-date" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-mono uppercase">Saatlik Ücret (TL)</Label>
              <Input type="number" step="0.01" min="0" value={form.hourly_wage ?? ""}
                onChange={(e) => setForm({ ...form, hourly_wage: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
                placeholder="öncelikli" data-testid="personnel-form-hourly" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Aylık Maaş (TL)</Label>
              <Input type="number" step="0.01" min="0" value={form.monthly_salary ?? ""}
                onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
                placeholder="saatlik boşsa kullanılır" data-testid="personnel-form-monthly" />
            </div>
          </div>
          <div className="border-2 border-[#09090B] p-2 space-y-2">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Pazartesi - Cuma</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-mono uppercase">Giriş</Label>
                <Input type="time" value={form.work_start || ""}
                  onChange={(e) => setForm({ ...form, work_start: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-work-start" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Çıkış</Label>
                <Input type="time" value={form.work_end || ""}
                  onChange={(e) => setForm({ ...form, work_end: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-work-end" />
              </div>
            </div>
          </div>
          <div className="border-2 border-[#09090B] p-2 space-y-2">
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Cumartesi</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-mono uppercase">Giriş</Label>
                <Input type="time" value={form.work_start_sat || ""}
                  onChange={(e) => setForm({ ...form, work_start_sat: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-work-start-sat" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Çıkış</Label>
                <Input type="time" value={form.work_end_sat || ""}
                  onChange={(e) => setForm({ ...form, work_end_sat: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-work-end-sat" />
              </div>
            </div>
            <div className="text-[10px] text-neutral-500">Boş bırakılırsa Cumartesi de Pzt-Cum saatleri geçerli olur.</div>
          </div>
          <div className="text-[11px] text-neutral-500 leading-relaxed">
            Pazar ve resmi tatiller ayrı hesaplandığı için ayrıca gün bazlı girilmez. Ücret önceliği: Saatlik &gt; Aylık maaş ÷ aylık standart saat &gt; Ayarlar varsayılanı. Çıkış toleransı Ayarlar &gt; Personel'den tüm personel için ortak girilir.
          </div>

          {/* Kişisel Bilgiler */}
          <div className="border-t-2 border-neutral-200 pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Kişisel Bilgiler</div>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-mono uppercase">Doğum Tarihi</Label>
                  <Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-birth-date" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Kimlik No</Label>
                  <Input value={form.id_no || ""} onChange={(e) => setForm({ ...form, id_no: e.target.value })}
                    placeholder="11 haneli T.C."
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-id-no" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-mono uppercase">Cinsiyet</Label>
                  <select value={form.gender || ""} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                    className="w-full h-9 rounded-none border-2 border-[#09090B] px-2 text-sm bg-white focus:outline-none" data-testid="personnel-form-gender">
                    <option value="">— Seçiniz —</option>
                    <option value="Erkek">Erkek</option>
                    <option value="Kadın">Kadın</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Medeni Hal</Label>
                  <select value={form.marital_status || ""} onChange={(e) => setForm({ ...form, marital_status: e.target.value })}
                    className="w-full h-9 rounded-none border-2 border-[#09090B] px-2 text-sm bg-white focus:outline-none" data-testid="personnel-form-marital">
                    <option value="">— Seçiniz —</option>
                    <option value="Bekar">Bekar</option>
                    <option value="Evli">Evli</option>
                    <option value="Boşanmış">Boşanmış</option>
                    <option value="Dul">Dul</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-mono uppercase">Uyruk</Label>
                  <Input value={form.nationality || ""} onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                    placeholder="Türk"
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-nationality" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Askerlik Durumu</Label>
                  <select value={form.military_status || ""} onChange={(e) => setForm({ ...form, military_status: e.target.value })}
                    className="w-full h-9 rounded-none border-2 border-[#09090B] px-2 text-sm bg-white focus:outline-none" data-testid="personnel-form-military">
                    <option value="">— Seçiniz —</option>
                    <option value="Yapıldı">Yapıldı</option>
                    <option value="Muaf">Muaf</option>
                    <option value="Tecilli">Tecilli</option>
                    <option value="İlgili Değil">İlgili Değil</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Ehliyet Sınıfı</Label>
                <Input value={form.driving_license || ""} onChange={(e) => setForm({ ...form, driving_license: e.target.value })}
                  placeholder="B, C, D..."
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-license" />
              </div>
            </div>
          </div>

          {/* İletişim & Kişisel Bilgiler */}
          <div className="border-t-2 border-neutral-200 pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">İletişim & Kişisel</div>
            <div className="space-y-2">
              <div>
                <Label className="text-xs font-mono uppercase">Adres</Label>
                <Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-address" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-mono uppercase">Cep Telefonu</Label>
                  <Input value={form.mobile_phone || ""} onChange={(e) => setForm({ ...form, mobile_phone: e.target.value })}
                    placeholder="0555 000 00 00"
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-mobile" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Kan Grubu</Label>
                  <Input value={form.blood_type || ""} onChange={(e) => setForm({ ...form, blood_type: e.target.value })}
                    placeholder="0 Rh+"
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-blood" />
                </div>
              </div>
            </div>
          </div>

          {/* Acil İletişim */}
          <div className="border-t-2 border-neutral-200 pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Acil İletişim</div>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-mono uppercase">Ad Soyad</Label>
                  <Input value={form.emergency_contact_name || ""} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-emc-name" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Yakınlık</Label>
                  <Input value={form.emergency_contact_relation || ""} onChange={(e) => setForm({ ...form, emergency_contact_relation: e.target.value })}
                    placeholder="Eş / Anne / Baba / Kardeş"
                    className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-emc-relation" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Cep Telefonu</Label>
                <Input value={form.emergency_contact_phone || ""} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
                  placeholder="0555 000 00 00"
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-emc-phone" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Notlar</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="personnel-form-notes" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Aktif personel
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={save} disabled={saving} data-testid="personnel-form-save">
            <Save size={14} className="mr-2" /> Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------- ATTENDANCE TAB ----------------------- */
function AttendanceTab() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "sub_admin";
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [people, setPeople] = useState([]);
  const [timesheet, setTimesheet] = useState({ per_day: [], summary: [], grand: {} });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [selectedPid, setSelectedPid] = useState(null);
  const [bulkAdvanceOpen, setBulkAdvanceOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [entriesRes, peopleRes, tsRes] = await Promise.all([
        api.get(`/personnel/attendance?year=${year}&month=${month}&_ts=${Date.now()}`),
        api.get(`/personnel/employees?_ts=${Date.now()}`),
        api.get(`/personnel/timesheet?year=${year}&month=${month}&_ts=${Date.now()}`),
      ]);
      setRows(entriesRes.data);
      setPeople(peopleRes.data);
      setTimesheet(tsRes.data || { per_day: [], summary: [], grand: {} });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const doPreview = async (file, importDate) => {
    const fd = new FormData();
    fd.append("file", file);
    if (importDate) fd.append("import_date", importDate);
    try {
      const { data } = await api.post("/personnel/attendance/preview-import", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadSample = async () => {
    try {
      const res = await api.get("/personnel/attendance/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "mesai_sablon.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      // fallback: CSV
      const blob = new Blob([sampleAttendanceCsv()], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "mesai_sablon.csv"; a.click();
      URL.revokeObjectURL(url);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`${r.personnel_name} — ${r.date} kaydını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/personnel/attendance/${r.id}`);
      toast.success("Kayıt silindi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Personel bazında özet (ay için giriş sayısı, ilk/son tarih, toplam çalışma saati)
  const perPersonSummary = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.personnel_id;
      const entry = map.get(key) || { count: 0, first: null, last: null };
      entry.count += 1;
      if (!entry.first || r.date < entry.first) entry.first = r.date;
      if (!entry.last  || r.date > entry.last)  entry.last  = r.date;
      map.set(key, entry);
    }
    return map;
  }, [rows]);

  const selectedPerson = useMemo(
    () => people.find((p) => p.id === selectedPid) || null,
    [people, selectedPid],
  );
  const selectedRows = useMemo(
    () => rows.filter((r) => r.personnel_id === selectedPid).sort((a, b) => a.date.localeCompare(b.date)),
    [rows, selectedPid],
  );
  const detailPage = usePagination(selectedRows, 10);

  // Puantaj map'i: attendance entry id → per_day satır (calc dahil)
  const tsByEntryId = useMemo(() => {
    const m = new Map();
    for (const r of timesheet.per_day || []) m.set(r.id, r);
    return m;
  }, [timesheet]);

  // Seçili personel için puantaj özeti (grand toplamları)
  const selectedSummary = useMemo(
    () => (timesheet.summary || []).find((s) => s.personnel_id === selectedPid) || null,
    [timesheet, selectedPid],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex items-end gap-3">
          <PeriodPicker year={year} month={month} onYear={setYear} onMonth={setMonth} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B]"
            onClick={async () => {
              const toastId = toast.loading("Toplu imza puantajı hazırlanıyor…");
              try {
                const res = await api.get(
                  `/personnel/signature-timesheet.pdf?year=${year}&month=${month}`,
                  { responseType: "blob", timeout: 240000 }
                );
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `imza_puantaji_toplu_${year}-${String(month).padStart(2, "0")}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                toast.dismiss(toastId);
                toast.success("Toplu imza puantajı indirildi");
              } catch (e) {
                toast.dismiss(toastId);
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PDF üretilemedi");
              }
            }}
            data-testid="bulk-signature-pdf-btn"
          >
            <FileText size={14} className="mr-2" /> Toplu İmza Puantajı (PDF)
          </Button>
          <Button variant="outline" className="rounded-none border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white"
            onClick={() => setBulkAdvanceOpen(true)} data-testid="bulk-advance-btn">
            <Wallet size={14} className="mr-2" /> Toplu Avans
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={downloadSample} data-testid="attendance-sample-btn">
                <Download size={14} className="mr-2" /> Örnek Şablon
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="attendance-file-input" />
              <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={() => setImportOpen(true)} data-testid="attendance-import-btn">
                <Upload size={14} className="mr-2" /> Excel Yükle
              </Button>
            </>
          )}
        </div>
      </div>

      {!selectedPid ? (
        <PersonnelPickerList
          people={people}
          summaryMap={perPersonSummary}
          loading={loading}
          onSelect={(pid) => setSelectedPid(pid)}
        />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 border-2 border-[#09090B] bg-neutral-50 p-3" data-testid="attendance-detail-header">
            <div className="flex items-center gap-3">
              <Button variant="outline" className="rounded-none border-2 border-[#09090B] h-9"
                onClick={() => setSelectedPid(null)} data-testid="attendance-back-btn">
                <ArrowLeft size={14} className="mr-1" /> Geri
              </Button>
              <div>
                <div className="font-black text-lg leading-tight">{selectedPerson?.name || "—"}</div>
                <div className="text-[11px] font-mono text-neutral-500">
                  Sicil: {selectedPerson?.employee_no || "—"} · {selectedRows.length} kayıt bu ay
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                className="rounded-none border-2 border-[#09090B]"
                onClick={async () => {
                  const toastId = toast.loading("İmza puantajı PDF üretiliyor…");
                  try {
                    const res = await api.get(
                      `/personnel/signature-timesheet.pdf?personnel_id=${selectedPid}&year=${year}&month=${month}`,
                      { responseType: "blob", timeout: 240000 }
                    );
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `imza_puantaji_${(selectedPerson?.name || "personel").replace(/\s+/g, "_")}_${year}-${String(month).padStart(2, "0")}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.dismiss(toastId);
                    toast.success("İmza puantajı indirildi");
                  } catch (e) {
                    toast.dismiss(toastId);
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PDF üretilemedi");
                  }
                }}
                data-testid="signature-timesheet-btn"
              >
                <FileText size={14} className="mr-2" /> İmza Puantajı İndir
              </Button>
              <Button className="rounded-none bg-[#09090B] hover:bg-[#0055FF]" onClick={() => setManualOpen(true)} data-testid="attendance-manual-btn" hidden={!canEdit} style={!canEdit ? { display: "none" } : undefined}>
                <Plus size={14} className="mr-2" /> Kayıt Ekle
              </Button>
            </div>
          </div>

          {selectedSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="timesheet-summary-boxes">
              <StatBox label="Toplam Çalışma" value={fmtMinsHM(selectedSummary.work_minutes)} />
              <StatBox label="Toplam Fazla Mesai" value={fmtMinsHM(selectedSummary.overtime_minutes)} tone="warn" />
              <StatBox label="Toplam Tatil Mesai" value={fmtMinsHM(selectedSummary.holiday_minutes)} tone="warn" />
              <StatBox label="Toplam Eksik" value={fmtMinsHM(selectedSummary.missing_minutes)} tone={selectedSummary.missing_minutes ? "err" : "ok"} />
              <StatBox label="Brüt Hak Ediş" value={fmtCurrency(selectedSummary.amount || 0)} data-testid="stat-gross-earnings" />
              <StatBox label="Toplam Avans" value={fmtCurrency(selectedSummary.advance_amount || 0)} tone={selectedSummary.advance_amount ? "warn" : undefined} data-testid="stat-advance" />
              <StatBox label="Net Hak Ediş" value={fmtCurrency(selectedSummary.net_amount ?? selectedSummary.amount)} tone="ok" data-testid="stat-net-earnings" />
            </div>
          )}

          <div className="bg-white border-2 border-[#09090B]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                  <TableHead className="font-mono uppercase text-xs">Tarih</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-center">Giriş</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-center">Çıkış</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Çalışma</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Fazla Mesai</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Tatil Mesai</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Eksik</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Günlük Hak Ediş</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Not</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-10 text-neutral-500 italic">
                      {loading ? "Yükleniyor…" : "Bu ay için kayıt yok"}
                    </TableCell>
                  </TableRow>
                ) : detailPage.paged.map((r) => {
                  if (r.is_leave) {
                    return (
                      <TableRow key={r.id} data-testid={`attendance-leave-row-${r.id}`} className="bg-amber-50">
                        <TableCell className="font-mono text-xs">{fmtDateWithDay(r.date)}</TableCell>
                        <TableCell colSpan={2} className="text-center">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#09090B] text-white text-[10px] font-mono uppercase" data-testid={`attendance-leave-badge-${r.id}`}>
                            {r.leave_type_label || "İzin"}
                          </span>
                          {r.deduct_pay ? (
                            <span className="ml-1 inline-block px-1.5 py-0.5 border border-amber-600 text-amber-800 text-[9px] font-mono uppercase">Ücret Kesintili</span>
                          ) : (
                            <span className="ml-1 inline-block px-1.5 py-0.5 border border-emerald-600 text-emerald-700 text-[9px] font-mono uppercase">Ücretli</span>
                          )}
                        </TableCell>
                        <TableCell colSpan={5} className="text-neutral-400 text-center text-xs italic">—</TableCell>
                        <TableCell className="text-xs text-neutral-500 max-w-[220px] truncate">{r.note || ""}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    );
                  }
                  const calc = tsByEntryId.get(r.id) || {};
                  return (
                  <TableRow key={r.id} data-testid={`attendance-row-${r.id}`}>
                    <TableCell className="font-mono text-xs">{fmtDateWithDay(r.date)}</TableCell>
                    <TableCell className="text-center font-mono">{r.check_in}</TableCell>
                    <TableCell className="text-center font-mono">{r.check_out}</TableCell>
                    <TableCell className="text-right font-mono text-xs" data-testid={`ts-work-${r.id}`}>{fmtMinsHM(calc.work_minutes || 0)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-yellow-700" data-testid={`ts-ot-${r.id}`}>{fmtMinsHM(calc.overtime_minutes || 0)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-orange-700" data-testid={`ts-hol-${r.id}`}>{fmtMinsHM(calc.holiday_minutes || 0)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-red-600" data-testid={`ts-missing-${r.id}`}>{fmtMinsHM(calc.missing_minutes || 0)}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-bold" data-testid={`ts-amount-${r.id}`}>{fmtCurrency(calc.amount || 0)}</TableCell>
                    <TableCell className="text-xs text-neutral-500 max-w-[220px] truncate">{r.note || ""}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <div className="flex gap-2 justify-end">
                          <Button size="icon" variant="outline" className="rounded-none border-2 border-[#09090B] h-8 w-8"
                            onClick={() => setEditingRow(r)} data-testid={`attendance-edit-${r.id}`}>
                            <Pencil size={14} />
                          </Button>
                          <Button size="icon" variant="outline" className="rounded-none border-2 border-red-600 text-red-600 h-8 w-8 hover:bg-red-600 hover:text-white"
                            onClick={() => del(r)} data-testid={`attendance-del-${r.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <Pager page={detailPage.page} setPage={detailPage.setPage} totalPages={detailPage.totalPages} testid="attendance-detail-pager" />

          <PersonnelAdvancesPanel
            personnelId={selectedPid}
            personnelName={selectedPerson?.name || ""}
            year={year}
            month={month}
            onChanged={load}
          />
        </div>
      )}

      {preview && (
        <PreviewImportDialog
          preview={preview}
          onClose={() => setPreview(null)}
          onDone={() => { setPreview(null); load(); }}
        />
      )}
      {manualOpen && (
        <ManualAttendanceDialog
          open={manualOpen}
          onOpenChange={setManualOpen}
          presetPersonnelId={selectedPid}
          onSaved={() => { setManualOpen(false); load(); }}
        />
      )}
      {editingRow && <EditAttendanceDialog row={editingRow} onClose={() => setEditingRow(null)} onSaved={() => { setEditingRow(null); load(); }} />}
      {bulkAdvanceOpen && (
        <BulkAdvanceDialog
          people={people}
          initialYear={year}
          initialMonth={month}
          onClose={() => setBulkAdvanceOpen(false)}
          onSaved={() => { setBulkAdvanceOpen(false); load(); }}
        />
      )}
      {importOpen && (
        <ImportAttendanceDialog
          initialYear={year}
          initialMonth={month}
          onClose={() => setImportOpen(false)}
          onSubmit={(file, date) => { setImportOpen(false); doPreview(file, date); }}
        />
      )}
    </div>
  );
}

function ImportAttendanceDialog({ initialYear, initialMonth, onClose, onSubmit }) {
  const defaultDate = `${initialYear}-${String(initialMonth).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
  const [date, setDate] = useState(defaultDate);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none border-2 border-[#09090B]">
        <DialogHeader><DialogTitle>Mesai Excel Yükle</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-neutral-600 leading-relaxed">
            Şablon sütunları: <b>Sicil No · Ad Soyad · Tarih · Giriş · Çıkış · Not</b>.
            Birden fazla güne ait kayıtlar farklı satırlar olarak tek dosyada yüklenebilir.
            Aşağıdaki "Varsayılan Tarih" isteğe bağlıdır — yalnız satırında "Tarih" boş olan
            kayıtlara uygulanır.
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Varsayılan Tarih (opsiyonel)</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="import-date" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Excel Dosyası (.xlsx) *</Label>
            <input ref={fileRef} type="file" accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm border-2 border-[#09090B] p-2 rounded-none"
              data-testid="import-file" />
            {file && <div className="text-[11px] font-mono text-neutral-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onClose}>İptal</Button>
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]"
            onClick={() => {
              if (!file) { toast.error("Excel dosyası seçin"); return; }
              onSubmit(file, date);
            }}
            data-testid="import-submit">
            <Upload size={14} className="mr-2" /> Önizle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PersonnelAdvancesPanel({ personnelId, personnelName, year, month, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/personnel/advances?personnel_id=${personnelId}&year=${year}&month=${month}&_ts=${Date.now()}`);
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [personnelId, year, month]);

  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const del = async (r) => {
    if (!window.confirm(`${r.date} tarihli ${r.amount} TL avans kaydını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/personnel/advances/${r.id}`);
      toast.success("Avans silindi");
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="advance-panel">
      <div className="flex items-center justify-between border-2 border-[#09090B] bg-emerald-50 p-3">
        <div className="flex items-center gap-3">
          <Wallet size={18} className="text-emerald-700" />
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-800">Avans</div>
            <div className="font-black text-lg leading-tight">Bu ay toplam: {fmtCurrency(total)}</div>
          </div>
        </div>
        <Button className="rounded-none bg-emerald-700 hover:bg-emerald-800" onClick={() => setOpen(true)} data-testid="advance-add-btn">
          <Plus size={14} className="mr-2" /> Avans Ekle
        </Button>
      </div>

      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Tarih</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">Tutar</TableHead>
              <TableHead className="font-mono uppercase text-xs">Açıklama</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-neutral-500 italic">
                  {loading ? "Yükleniyor…" : "Bu ay için avans kaydı yok"}
                </TableCell>
              </TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`advance-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.date}</TableCell>
                <TableCell className="text-right font-mono font-bold text-emerald-700">{fmtCurrency(r.amount)}</TableCell>
                <TableCell className="text-xs text-neutral-600 max-w-[420px] truncate">{r.description || ""}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="outline" className="rounded-none border-2 border-red-600 text-red-600 h-8 w-8 hover:bg-red-600 hover:text-white"
                    onClick={() => del(r)} data-testid={`advance-del-${r.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {open && (
        <AdvanceDialog
          personnelId={personnelId}
          personnelName={personnelName}
          year={year} month={month}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

function AdvanceDialog({ personnelId, personnelName, year, month, onClose, onSaved }) {
  const defaultDate = `${year}-${String(month).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
  const [form, setForm] = useState({ date: defaultDate, amount: "", description: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (saving) return;
    const amount = Number(form.amount);
    if (!form.date) { toast.error("Tarih zorunlu"); return; }
    if (!amount || amount <= 0) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    setSaving(true);
    try {
      await api.post("/personnel/advances", {
        personnel_id: personnelId,
        date: form.date,
        amount,
        description: form.description || "",
      });
      toast.success("Avans eklendi");
      // Formu sıfırla (yeniden açıldığında temiz başlasın)
      setForm({ date: defaultDate, amount: "", description: "" });
      // Kaydet butonu bu noktadan sonra tıklanamaz kalır — dialog kapanıncaya kadar
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none border-2 border-[#09090B]">
        <DialogHeader><DialogTitle>Avans Ekle · {personnelName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-mono uppercase">Tarih *</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="advance-date" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Tutar (TL) *</Label>
            <Input type="number" step="0.01" min="0" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="advance-amount" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Açıklama</Label>
            <Textarea rows={2} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Örn: yakıt, market"
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="advance-desc" />
          </div>
          <div className="text-[11px] text-neutral-500">
            Bu avans, personelin bu aya ait puantaj hak edişinden otomatik düşülecek.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onClose}>İptal</Button>
          <Button className="rounded-none bg-emerald-700 hover:bg-emerald-800" onClick={save} disabled={saving} data-testid="advance-save">
            <Save size={14} className="mr-2" /> Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkAdvanceDialog({ people, initialYear, initialMonth, onClose, onSaved }) {
  const defaultDate = `${initialYear}-${String(initialMonth).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
  const [date, setDate] = useState(defaultDate);
  const [entries, setEntries] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [unmatched, setUnmatched] = useState([]);
  const fileRef = useRef(null);

  const filtered = useMemo(() => {
    if (!q) return people;
    const s = q.toLocaleLowerCase("tr-TR");
    return people.filter((p) =>
      (p.name || "").toLocaleLowerCase("tr-TR").includes(s) ||
      (p.employee_no || "").toLocaleLowerCase("tr-TR").includes(s),
    );
  }, [people, q]);

  const setField = (pid, field, val) => {
    setEntries((prev) => ({ ...prev, [pid]: { ...prev[pid], [field]: val } }));
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/personnel/advances/bulk-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "toplu_avans_sablon.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    setImporting(true);
    setUnmatched([]);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/personnel/advances/bulk/parse-xlsx", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      // matched → form state'e yaz
      const next = { ...entries };
      for (const m of data.matched) {
        next[m.personnel_id] = { amount: String(m.amount), description: m.description || "" };
      }
      setEntries(next);
      setUnmatched(data.unmatched || []);
      toast.success(`${data.matched.length} satır yüklendi${data.unmatched?.length ? ` · ${data.unmatched.length} eşleşmedi` : ""}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const validEntries = Object.entries(entries).filter(([, v]) => Number(v?.amount) > 0);
  const total = validEntries.reduce((s, [, v]) => s + Number(v.amount || 0), 0);

  const save = async () => {
    if (!date) { toast.error("Tarih zorunlu"); return; }
    if (validEntries.length === 0) { toast.error("En az bir personel için tutar girin"); return; }
    setSaving(true);
    try {
      const items = validEntries.map(([pid, v]) => ({
        personnel_id: pid,
        amount: Number(v.amount),
        description: (v.description || "").trim(),
      }));
      const { data } = await api.post("/personnel/advances/bulk", { date, items });
      toast.success(`${data.inserted} personel için toplam ${fmtCurrency(data.total_amount)} avans eklendi`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none border-2 border-[#09090B] max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle>Toplu Avans Girişi</DialogTitle></DialogHeader>
        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          <div className="flex flex-wrap items-end gap-2 justify-between">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs font-mono uppercase">Tarih *</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="bulk-advance-date" />
              </div>
              <div className="min-w-[240px]">
                <Label className="text-xs font-mono uppercase">Ara</Label>
                <Input placeholder="Ad veya sicil…" value={q} onChange={(e) => setQ(e.target.value)}
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="bulk-advance-search" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={downloadTemplate} data-testid="bulk-advance-template">
                <Download size={14} className="mr-2" /> Örnek Şablon
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onImportFile} data-testid="bulk-advance-file-input" />
              <Button variant="outline" className="rounded-none border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white"
                onClick={() => fileRef.current?.click()} disabled={importing} data-testid="bulk-advance-import">
                <Upload size={14} className="mr-2" /> {importing ? "Yükleniyor…" : "Excel ile Yükle"}
              </Button>
            </div>
          </div>

          {unmatched.length > 0 && (
            <div className="border-2 border-yellow-500 bg-yellow-50 p-3 text-xs" data-testid="bulk-advance-unmatched">
              <div className="font-bold mb-1 flex items-center gap-1 text-yellow-800">
                <AlertTriangle size={14} /> Eşleşmeyen / Atlanan Satırlar ({unmatched.length})
              </div>
              <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                {unmatched.map((u, i) => (
                  <li key={i}>Satır {u.row}: {u.employee_no || u.name || "?"} — {u.reason}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-2 border-[#09090B] overflow-y-auto flex-1" data-testid="bulk-advance-list">
            <Table>
              <TableHeader className="sticky top-0 bg-neutral-100 z-10">
                <TableRow className="border-b-2 border-[#09090B]">
                  <TableHead className="font-mono uppercase text-xs">Sicil</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Ad Soyad</TableHead>
                  <TableHead className="font-mono uppercase text-xs w-40">Tutar (TL)</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Açıklama</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-6 italic text-neutral-500">Personel bulunamadı</TableCell>
                  </TableRow>
                ) : filtered.map((p) => {
                  const cur = entries[p.id] || {};
                  const has = Number(cur.amount) > 0;
                  return (
                    <TableRow key={p.id} data-testid={`bulk-advance-row-${p.id}`}
                      className={has ? "bg-emerald-50" : ""}>
                      <TableCell className="font-mono text-xs">{p.employee_no || "—"}</TableCell>
                      <TableCell className="font-semibold text-sm">{p.name}</TableCell>
                      <TableCell>
                        <Input type="number" step="0.01" min="0" value={cur.amount || ""}
                          onChange={(e) => setField(p.id, "amount", e.target.value)}
                          placeholder="0,00"
                          className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-9"
                          data-testid={`bulk-advance-amount-${p.id}`} />
                      </TableCell>
                      <TableCell>
                        <Input value={cur.description || ""}
                          onChange={(e) => setField(p.id, "description", e.target.value)}
                          placeholder="Örn: yakıt, market"
                          className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 h-9"
                          data-testid={`bulk-advance-desc-${p.id}`} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-between items-center text-xs font-mono border-2 border-emerald-700 bg-emerald-50 p-2">
            <div className="text-emerald-800">
              {validEntries.length} personel için giriş yapıldı
            </div>
            <div className="font-black text-emerald-900 text-base" data-testid="bulk-advance-total">
              Toplam: {fmtCurrency(total)}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onClose}>İptal</Button>
          <Button className="rounded-none bg-emerald-700 hover:bg-emerald-800"
            onClick={save} disabled={saving || validEntries.length === 0}
            data-testid="bulk-advance-save">
            <Save size={14} className="mr-2" /> Kaydet ({validEntries.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PersonnelPickerList({ people, summaryMap, loading, onSelect }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q) return people;
    const s = q.toLocaleLowerCase("tr-TR");
    return people.filter((p) =>
      (p.name || "").toLocaleLowerCase("tr-TR").includes(s) ||
      (p.employee_no || "").toLocaleLowerCase("tr-TR").includes(s)
    );
  }, [people, q]);
  const { page, setPage, totalPages, paged } = usePagination(filtered, 10);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input placeholder="Personel ara (ad veya sicil)…" value={q} onChange={(e) => setQ(e.target.value)}
          className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 max-w-sm" data-testid="attendance-search" />
        <div className="text-xs text-neutral-500 font-mono">
          Bu ay {summaryMap.size} personel kayıtlı · toplam {people.length} personel
        </div>
      </div>
      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Sicil</TableHead>
              <TableHead className="font-mono uppercase text-xs">Ad Soyad</TableHead>
              <TableHead className="font-mono uppercase text-xs text-center">Kayıt</TableHead>
              <TableHead className="font-mono uppercase text-xs">İlk / Son Tarih</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">Aç</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-neutral-500 italic">
                  {loading ? "Yükleniyor…" : "Personel bulunamadı"}
                </TableCell>
              </TableRow>
            ) : paged.map((p) => {
              const stat = summaryMap.get(p.id);
              const has = !!stat;
              return (
                <TableRow
                  key={p.id}
                  data-testid={`attendance-person-${p.id}`}
                  className={`cursor-pointer ${has ? "hover:bg-[#0055FF]/5" : "hover:bg-neutral-50"}`}
                  onClick={() => onSelect(p.id)}
                >
                  <TableCell className="font-mono text-xs">{p.employee_no || "—"}</TableCell>
                  <TableCell className="font-semibold">{p.name}</TableCell>
                  <TableCell className="text-center">
                    {has ? (
                      <span className="inline-block px-2 py-0.5 border-2 border-[#0055FF] text-[#0055FF] text-xs font-mono font-bold">
                        {stat.count}
                      </span>
                    ) : (
                      <span className="text-neutral-400 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-neutral-600">
                    {has ? `${stat.first} → ${stat.last}` : ""}
                  </TableCell>
                  <TableCell className="text-right">
                    <ChevronRight size={16} className="inline text-neutral-500" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <Pager page={page} setPage={setPage} totalPages={totalPages} testid="attendance-picker-pager" />
    </div>
  );
}

function sampleAttendanceCsv() {
  // Yeni format: Sicil No, Ad Soyad, Giriş, Çıkış, Not — tarih yükleme sırasında seçilir
  return "Sicil No,Ad Soyad,Giriş,Çıkış,Not\n" +
         "001,Ali Yılmaz,08:00,17:00,\n" +
         "002,Ayşe Demir,08:00,19:30,fazla mesai\n";
}

function PeriodPicker({ year, month, onYear, onMonth }) {
  const months = [
    "Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
    "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık",
  ];
  const years = [];
  const y = new Date().getFullYear();
  for (let i = y - 3; i <= y + 1; i++) years.push(i);
  return (
    <div className="flex gap-2">
      <div>
        <Label className="text-xs font-mono uppercase">Yıl</Label>
        <select value={year} onChange={(e) => onYear(+e.target.value)}
          className="block rounded-none border-2 border-[#09090B] h-10 px-2" data-testid="period-year">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-xs font-mono uppercase">Ay</Label>
        <select value={month} onChange={(e) => onMonth(+e.target.value)}
          className="block rounded-none border-2 border-[#09090B] h-10 px-2" data-testid="period-month">
          {months.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      </div>
    </div>
  );
}

function ManualAttendanceDialog({ open, onOpenChange, presetPersonnelId, onSaved }) {
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({
    personnel_id: presetPersonnelId || "", date: getTrToday(),
    check_in: "08:00", check_out: "17:30", note: "",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/personnel/employees").then((r) => setPeople(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (presetPersonnelId) setForm((f) => ({ ...f, personnel_id: presetPersonnelId }));
  }, [presetPersonnelId]);

  const save = async () => {
    if (!form.personnel_id) { toast.error("Personel seçin"); return; }
    setSaving(true);
    try {
      await api.post("/personnel/attendance", form);
      toast.success("Kayıt eklendi");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-[#09090B]">
        <DialogHeader><DialogTitle>Manuel Mesai Kaydı</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-mono uppercase">Personel *</Label>
            <select className="w-full block rounded-none border-2 border-[#09090B] h-10 px-2"
              value={form.personnel_id} onChange={(e) => setForm({ ...form, personnel_id: e.target.value })}
              data-testid="manual-personnel">
              <option value="">-- seçin --</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.employee_no ? ` (${p.employee_no})` : ""}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs font-mono uppercase">Tarih</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="manual-date" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Giriş</Label>
              <Input type="time" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="manual-in" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Çıkış</Label>
              <Input type="time" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="manual-out" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Not</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={save} disabled={saving} data-testid="manual-save">
            <Save size={14} className="mr-2" /> Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditAttendanceDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState({ check_in: row.check_in, check_out: row.check_out, note: row.note || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/personnel/attendance/${row.id}`, form);
      toast.success("Güncellendi");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none border-2 border-[#09090B]">
        <DialogHeader><DialogTitle>Kayıt Düzenle · {row.personnel_name} · {row.date}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs font-mono uppercase">Giriş</Label>
            <Input type="time" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Çıkış</Label>
            <Input type="time" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Not</Label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onClose}>İptal</Button>
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={save} disabled={saving}>
            <Save size={14} className="mr-2" /> Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewImportDialog({ preview, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const conflictRows = preview.rows.filter((r) => r.conflict);
  const okRows = preview.rows.filter((r) => !r.conflict);

  const doImport = async (overwrite) => {
    setBusy(true);
    try {
      const rows = overwrite ? preview.rows : okRows;
      const payload = {
        overwrite,
        rows: rows.map((r) => ({
          personnel_id: r.personnel_id, date: r.date,
          check_in: r.check_in, check_out: r.check_out, note: r.note || "",
        })),
      };
      const { data } = await api.post("/personnel/attendance/import", payload);
      toast.success(`Eklendi: ${data.inserted} · Güncellendi: ${data.updated} · Atlandı: ${data.skipped}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-none border-2 border-[#09090B] max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Excel İçe Aktarma Önizleme</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-3 gap-2">
            <StatBox label="Geçerli Satır" value={preview.total} />
            <StatBox label="Çakışma" value={preview.conflict_count} tone={preview.conflict_count ? "warn" : "ok"} />
            <StatBox label="Hata" value={preview.errors?.length || 0} tone={preview.errors?.length ? "err" : "ok"} />
          </div>
          {preview.errors?.length > 0 && (
            <div className="border-2 border-red-500 bg-red-50 p-3 text-xs" data-testid="preview-errors">
              <div className="font-bold mb-1 flex items-center gap-1 text-red-700">
                <AlertTriangle size={14} /> Atılan Satırlar
              </div>
              <ul className="space-y-1 max-h-32 overflow-y-auto">
                {preview.errors.map((e, i) => (
                  <li key={i}>Satır {e.row}: {e.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {conflictRows.length > 0 && (
            <div className="border-2 border-yellow-500 bg-yellow-50 p-3 text-xs" data-testid="preview-conflicts">
              <div className="font-bold mb-1 flex items-center gap-1 text-yellow-800">
                <AlertTriangle size={14} /> Çakışan Kayıtlar ({conflictRows.length})
              </div>
              <div className="text-yellow-900 mb-2">Bu tarihler için mevcut kayıtlar var. "Üzerine Yaz" ile yeni verilerle güncellenirler.</div>
              <table className="w-full text-[11px] border-collapse">
                <thead className="bg-yellow-100">
                  <tr>
                    <th className="border p-1">Personel</th><th className="border p-1">Tarih</th>
                    <th className="border p-1">Mevcut</th><th className="border p-1">Yeni</th>
                  </tr>
                </thead>
                <tbody>
                  {conflictRows.map((r, i) => (
                    <tr key={i}>
                      <td className="border p-1">{r.personnel_name}</td>
                      <td className="border p-1 font-mono">{r.date}</td>
                      <td className="border p-1 font-mono">{r.existing?.check_in}–{r.existing?.check_out}</td>
                      <td className="border p-1 font-mono">{r.check_in}–{r.check_out}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="border-2 border-[#09090B]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-100">
                  <TableHead className="text-xs">Satır</TableHead>
                  <TableHead className="text-xs">Personel</TableHead>
                  <TableHead className="text-xs">Tarih</TableHead>
                  <TableHead className="text-xs">Giriş</TableHead>
                  <TableHead className="text-xs">Çıkış</TableHead>
                  <TableHead className="text-xs">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.slice(0, 200).map((r, i) => (
                  <TableRow key={i} className={r.conflict ? "bg-yellow-50" : ""}>
                    <TableCell className="text-xs font-mono">{r.row}</TableCell>
                    <TableCell className="text-xs">{r.personnel_name}</TableCell>
                    <TableCell className="text-xs font-mono">{fmtDateWithDay(r.date)}</TableCell>
                    <TableCell className="text-xs font-mono">{r.check_in}</TableCell>
                    <TableCell className="text-xs font-mono">{r.check_out}</TableCell>
                    <TableCell className="text-xs">
                      {r.conflict
                        ? <span className="text-yellow-700 font-mono">ÇAKIŞMA</span>
                        : <span className="text-emerald-700 font-mono">YENİ</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {preview.rows.length > 200 && (
              <div className="p-2 text-center text-xs text-neutral-500">…ilk 200 satır gösteriliyor</div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={onClose}>Vazgeç</Button>
          <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => doImport(false)} disabled={busy || okRows.length === 0} data-testid="preview-import-skip">
            Çakışanları Atla ve İçe Aktar ({okRows.length})
          </Button>
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={() => doImport(true)} disabled={busy || preview.rows.length === 0} data-testid="preview-import-overwrite">
            Üzerine Yaz ve İçe Aktar ({preview.rows.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatBox({ label, value, tone }) {
  const cls =
    tone === "err"  ? "border-red-500 text-red-700"    :
    tone === "warn" ? "border-yellow-500 text-yellow-800" :
                      "border-[#09090B] text-[#09090B]";
  return (
    <div className={`border-2 p-3 ${cls}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest">{label}</div>
      <div className="text-2xl font-black">{value}</div>
    </div>
  );
}

/* ----------------------- HOLIDAYS TAB ----------------------- */
function HolidaysTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", repeating: false, half_day: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/personnel/holidays?_ts=${Date.now()}`);
      setRows(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.date || !form.name.trim()) { toast.error("Tarih ve ad zorunlu"); return; }
    try {
      await api.post("/personnel/holidays", form);
      toast.success("Tatil eklendi");
      setForm({ date: "", name: "", repeating: false, half_day: false });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm(`"${r.name}" tatilini silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/personnel/holidays/${r.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-[#09090B] p-4 bg-white">
        <div className="text-xs font-mono uppercase tracking-widest mb-2 text-neutral-500">Yeni Tatil</div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs font-mono uppercase">Tarih</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="holiday-date" />
          </div>
          <div className="min-w-[240px]">
            <Label className="text-xs font-mono uppercase">Adı</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Örn. 23 Nisan"
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="holiday-name" />
          </div>
          <label className="flex items-center gap-2 text-xs h-10 pt-6">
            <input type="checkbox" checked={form.repeating} onChange={(e) => setForm({ ...form, repeating: e.target.checked })} data-testid="holiday-repeating" />
            Her yıl tekrar
          </label>
          <label className="flex items-center gap-2 text-xs h-10 pt-6">
            <input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} data-testid="holiday-half-day" />
            Yarım gün
          </label>
          <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={add} data-testid="holiday-add">
            <Plus size={14} className="mr-2" /> Ekle
          </Button>
        </div>
      </div>

      <div className="bg-white border-2 border-[#09090B]">
        <Table>
          <TableHeader>
            <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
              <TableHead className="font-mono uppercase text-xs">Tarih</TableHead>
              <TableHead className="font-mono uppercase text-xs">Ad</TableHead>
              <TableHead className="font-mono uppercase text-xs text-center">Süre</TableHead>
              <TableHead className="font-mono uppercase text-xs text-center">Tekrar</TableHead>
              <TableHead className="font-mono uppercase text-xs text-right">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-neutral-500 italic">
                  {loading ? "Yükleniyor…" : "Tatil kaydı yok"}
                </TableCell>
              </TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} data-testid={`holiday-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.date}</TableCell>
                <TableCell className="font-semibold">{r.name}</TableCell>
                <TableCell className="text-center">
                  {r.half_day ? (
                    <span className="inline-block px-2 py-0.5 border border-amber-600 text-amber-700 text-[10px] font-mono uppercase" data-testid={`holiday-halfday-${r.id}`}>Yarım Gün</span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 border border-[#09090B] text-[#09090B] text-[10px] font-mono uppercase">Tam Gün</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {r.repeating ? (
                    <span className="inline-block px-2 py-0.5 border border-emerald-600 text-emerald-700 text-[10px] font-mono uppercase">Yıllık</span>
                  ) : (
                    <span className="text-neutral-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="outline" className="rounded-none border-2 border-red-600 text-red-600 h-8 w-8 hover:bg-red-600 hover:text-white"
                    onClick={() => del(r)} data-testid={`holiday-del-${r.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* ----------------------- SETTINGS TAB ----------------------- */
function PersonnelSettingsTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/personnel/settings");
      setForm(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/personnel/settings", {
        work_start: form.work_start, work_end: form.work_end,
        overtime_rate_pct: Number(form.overtime_rate_pct) || 0,
        holiday_rate_pct: Number(form.holiday_rate_pct) || 0,
        sunday_rate_pct: Number(form.sunday_rate_pct) || 0,
        overtime_tolerance_mins: Math.max(0, parseInt(form.overtime_tolerance_mins ?? 0, 10) || 0),
        standard_hourly_wage: Number(form.standard_hourly_wage) || 0,
        monthly_standard_hours: Number(form.monthly_standard_hours) || 225,
        weekday_standard_hours:  Number(form.weekday_standard_hours)  || 8,
        saturday_standard_hours: Number(form.saturday_standard_hours) || 5,
        morning_tea_start:   form.morning_tea_start   || "10:00",
        morning_tea_end:     form.morning_tea_end     || "10:15",
        lunch_start:         form.lunch_start         || "11:30",
        lunch_end:           form.lunch_end           || "12:30",
        afternoon_tea_start: form.afternoon_tea_start || "15:30",
        afternoon_tea_end:   form.afternoon_tea_end   || "15:45",
        saturday_tea_start:  form.saturday_tea_start  || "10:00",
        saturday_tea_end:    form.saturday_tea_end    || "10:30",
        signature_company_name: (form.signature_company_name || "").trim(),
        signature_blocks: (form.signature_blocks || []).map((b) => (b || "").trim()).filter(Boolean),
        color_normal_worked:  form.color_normal_worked  || "FFFFFF",
        color_absent:         form.color_absent         || "FEE2E2",
        color_sunday_off:     form.color_sunday_off     || "FEF3C7",
        color_sunday_worked:  form.color_sunday_worked  || "FDE68A",
        color_holiday_off:    form.color_holiday_off    || "DBEAFE",
        color_holiday_worked: form.color_holiday_worked || "BFDBFE",
        text_color_normal_worked:  form.text_color_normal_worked  || "000000",
        text_color_absent:         form.text_color_absent         || "000000",
        text_color_sunday_off:     form.text_color_sunday_off     || "000000",
        text_color_sunday_worked:  form.text_color_sunday_worked  || "000000",
        text_color_holiday_off:    form.text_color_holiday_off    || "000000",
        text_color_holiday_worked: form.text_color_holiday_worked || "000000",
      });
      toast.success("Ayarlar kaydedildi");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/personnel/settings/signature-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo yüklendi");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clearLogo = async () => {
    if (!window.confirm("Logo kaldırılsın mı?")) return;
    try {
      await api.delete("/personnel/settings/signature-logo");
      toast.success("Logo kaldırıldı");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!form) return <div className="p-6 text-sm text-neutral-500">Yükleniyor…</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="border-2 border-[#09090B] p-5 bg-white space-y-4">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Standart Çalışma</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-mono uppercase">Başlangıç Saati</Label>
            <Input type="time" value={form.work_start} onChange={(e) => setForm({ ...form, work_start: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-work-start" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Bitiş Saati</Label>
            <Input type="time" value={form.work_end} onChange={(e) => setForm({ ...form, work_end: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-work-end" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs font-mono uppercase">Mesai Zammı (%)</Label>
            <Input type="number" step="1" value={form.overtime_rate_pct}
              onChange={(e) => setForm({ ...form, overtime_rate_pct: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-ot-pct" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Tatil Zammı (%)</Label>
            <Input type="number" step="1" value={form.holiday_rate_pct}
              onChange={(e) => setForm({ ...form, holiday_rate_pct: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-hol-pct" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Saatlik Ücret (TL)</Label>
            <Input type="number" step="0.01" value={form.standard_hourly_wage}
              onChange={(e) => setForm({ ...form, standard_hourly_wage: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-hourly-wage" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-mono uppercase">Hafta İçi Günlük Standart Saat</Label>
            <Input type="number" step="0.01" min="0" value={form.weekday_standard_hours ?? 8}
              onChange={(e) => setForm({ ...form, weekday_standard_hours: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-weekday-std" />
            <div className="text-[10px] text-neutral-500 mt-1">Varsayılan: 8,00 saat</div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Cumartesi Günlük Standart Saat</Label>
            <Input type="number" step="0.01" min="0" value={form.saturday_standard_hours ?? 5}
              onChange={(e) => setForm({ ...form, saturday_standard_hours: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-saturday-std" />
            <div className="text-[10px] text-neutral-500 mt-1">Varsayılan: 5,00 saat</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-mono uppercase">Aylık Standart Çalışma Saati</Label>
            <Input type="number" step="1" min="1" value={form.monthly_standard_hours ?? 225}
              onChange={(e) => setForm({ ...form, monthly_standard_hours: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-monthly-std-hours" />
            <div className="text-[10px] text-neutral-500 mt-1">Aylık maaş / bu değer = saatlik ücret (personel bazlı saatlik yoksa).</div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Çıkış Toleransı (dk)</Label>
            <Input type="number" step="1" min="0" value={form.overtime_tolerance_mins ?? 0}
              onChange={(e) => setForm({ ...form, overtime_tolerance_mins: e.target.value })}
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-tolerance" />
            <div className="text-[10px] text-neutral-500 mt-1 leading-relaxed">
              Tüm personel için ortak. Standart çıkış saatinden sonra bu dakika içinde yapılan çıkışlar standart bitiş sayılır; aşan süre fazla mesaiye yazılır. Erken giriş dikkate alınmaz.
            </div>
          </div>
        </div>
        <div className="text-[11px] text-neutral-500">
          Örnek: %50 mesai zammı → fazla mesai saatinin ücreti = normal × 1.50 · %100 tatil zammı → 2.00×
        </div>

        <div className="border-t-2 border-[#09090B] pt-4 space-y-3" data-testid="break-settings-section">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Mola Saatleri</div>
          <div className="text-[11px] text-neutral-500 leading-relaxed">
            Puantajda dinamik mola: personel mola başlamadan önce giriş yaptıysa VE mola bitmeden önce çıkmadıysa o mola uygulanır. Toplam mola = geçerli tüm molaların dakika toplamı. Cumartesi yalnızca tek çay saati kullanılır.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-mono uppercase">Sabah Çayı Başlangıç</Label>
              <Input type="time" value={form.morning_tea_start || "10:00"}
                onChange={(e) => setForm({ ...form, morning_tea_start: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-morning-tea-start" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Sabah Çayı Bitiş</Label>
              <Input type="time" value={form.morning_tea_end || "10:15"}
                onChange={(e) => setForm({ ...form, morning_tea_end: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-morning-tea-end" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Öğle Yemeği Başlangıç</Label>
              <Input type="time" value={form.lunch_start || "11:30"}
                onChange={(e) => setForm({ ...form, lunch_start: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-lunch-start" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Öğle Yemeği Bitiş</Label>
              <Input type="time" value={form.lunch_end || "12:30"}
                onChange={(e) => setForm({ ...form, lunch_end: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-lunch-end" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">İkindi Çayı Başlangıç</Label>
              <Input type="time" value={form.afternoon_tea_start || "15:30"}
                onChange={(e) => setForm({ ...form, afternoon_tea_start: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-afternoon-tea-start" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">İkindi Çayı Bitiş</Label>
              <Input type="time" value={form.afternoon_tea_end || "15:45"}
                onChange={(e) => setForm({ ...form, afternoon_tea_end: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-afternoon-tea-end" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Cumartesi Çay Başlangıç</Label>
              <Input type="time" value={form.saturday_tea_start || "10:00"}
                onChange={(e) => setForm({ ...form, saturday_tea_start: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-saturday-tea-start" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Cumartesi Çay Bitiş</Label>
              <Input type="time" value={form.saturday_tea_end || "10:30"}
                onChange={(e) => setForm({ ...form, saturday_tea_end: e.target.value })}
                className="rounded-none border-2 border-[#09090B] focus-visible:ring-0" data-testid="settings-saturday-tea-end" />
            </div>
          </div>
        </div>

        <Button className="rounded-none bg-[#0055FF] hover:bg-[#09090B]" onClick={save} disabled={saving} data-testid="settings-save">
          <Save size={14} className="mr-2" /> Ayarları Kaydet
        </Button>
      </div>

      <div className="border-2 border-[#09090B] p-5 bg-white space-y-4" data-testid="signature-template-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">İmza Puantaj Şablonu</div>
            <div className="text-[11px] text-neutral-500 mt-1">
              Personel ve ay bazlı imza puantajı Excel çıktısı için şablon ayarları. Ücret / hak ediş bilgisi çıktıda yer almaz.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-mono uppercase">Şirket Adı</Label>
            <Input
              value={form.signature_company_name || ""}
              onChange={(e) => setForm({ ...form, signature_company_name: e.target.value })}
              placeholder="Örn: Hakpi A.Ş."
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
              data-testid="settings-signature-company-name"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">Şirket Logosu</Label>
            <div className="flex items-center gap-2">
              {form.signature_logo_b64 ? (
                <div className="flex items-center gap-2 border-2 border-[#09090B] px-2 py-1 bg-neutral-50">
                  <ImageIcon size={14} className="text-emerald-700" />
                  <span className="text-[11px] font-mono">Mevcut logo</span>
                  <button
                    type="button"
                    className="text-red-700 hover:text-red-900"
                    onClick={clearLogo}
                    data-testid="settings-signature-logo-clear"
                    title="Logoyu kaldır"
                  >
                    <XIcon size={14} />
                  </button>
                </div>
              ) : null}
              <label className="inline-flex items-center gap-2 border-2 border-[#09090B] px-3 py-2 cursor-pointer hover:bg-neutral-100 text-sm" data-testid="settings-signature-logo-upload">
                <Upload size={14} /> {form.signature_logo_b64 ? "Değiştir" : "Logo Yükle"}
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
                />
              </label>
            </div>
            <div className="text-[10px] text-neutral-500 mt-1">PNG / JPG / WEBP · maks 2 MB</div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs font-mono uppercase">İmza Alanları (yan yana kutucuklar)</Label>
            <Button
              size="sm"
              variant="outline"
              className="rounded-none border-2 border-[#09090B] h-8"
              onClick={() => setForm({ ...form, signature_blocks: [...(form.signature_blocks || []), ""] })}
              data-testid="settings-signature-block-add"
            >
              <Plus size={12} className="mr-1" /> Ekle
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(form.signature_blocks || []).map((lbl, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`settings-signature-block-row-${i}`}>
                <Input
                  value={lbl}
                  onChange={(e) => {
                    const copy = [...(form.signature_blocks || [])];
                    copy[i] = e.target.value;
                    setForm({ ...form, signature_blocks: copy });
                  }}
                  placeholder="Örn: İşveren, Muhasebe, Personel"
                  className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none border-2 border-red-700 text-red-700 hover:bg-red-50 h-9"
                  onClick={() => {
                    const copy = [...(form.signature_blocks || [])];
                    copy.splice(i, 1);
                    setForm({ ...form, signature_blocks: copy });
                  }}
                  data-testid={`settings-signature-block-del-${i}`}
                >
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
            {(!form.signature_blocks || form.signature_blocks.length === 0) && (
              <div className="text-[11px] text-neutral-500 italic">Henüz blok eklenmedi. Varsayılan: "İşveren" ve "Muhasebe".</div>
            )}
          </div>
        </div>

        <div className="text-[11px] text-neutral-500">
          Değişiklikleri kaydettikten sonra puantaj ekranında bir personeli seçip "İmza Puantajı İndir" butonundan Excel çıktısı alabilirsiniz.
        </div>

        <div className="border-t-2 border-dashed border-[#09090B] pt-4 space-y-3" data-testid="signature-day-colors-section">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Gün Tipi Renkleri</div>
          <div className="text-[11px] text-neutral-500 leading-relaxed">
            PDF / Excel çıktısında her satırın arka plan rengi gün tipine göre bu ayarlardan okunur: giriş-çıkış kaydı varsa çalışılan rengi, yoksa çalışılmayan rengi uygulanır.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { k: "color_normal_worked",  tk: "text_color_normal_worked",  label: "Normal çalışılan gün",       def: "FFFFFF", tdef: "000000" },
              { k: "color_absent",         tk: "text_color_absent",         label: "Devamsızlık (kayıt yok)",     def: "FEE2E2", tdef: "000000" },
              { k: "color_sunday_off",     tk: "text_color_sunday_off",     label: "Pazar · çalışılmayan",       def: "FEF3C7", tdef: "000000" },
              { k: "color_sunday_worked",  tk: "text_color_sunday_worked",  label: "Pazar · çalışılan",          def: "FDE68A", tdef: "000000" },
              { k: "color_holiday_off",    tk: "text_color_holiday_off",    label: "Resmi tatil · çalışılmayan", def: "DBEAFE", tdef: "000000" },
              { k: "color_holiday_worked", tk: "text_color_holiday_worked", label: "Resmi tatil · çalışılan",    def: "BFDBFE", tdef: "000000" },
            ].map((c) => {
              const bg = (form[c.k] || c.def).replace(/^#/, "").toUpperCase();
              const tx = (form[c.tk] || c.tdef).replace(/^#/, "").toUpperCase();
              return (
                <div key={c.k} className="border-2 border-[#09090B] p-2 space-y-2" data-testid={`color-row-${c.k}`}>
                  <div className="text-[11px] font-semibold">{c.label}</div>
                  <div
                    className="h-8 flex items-center justify-center border border-neutral-300 text-[11px] font-mono"
                    style={{ backgroundColor: `#${bg}`, color: `#${tx}` }}
                    data-testid={`color-preview-${c.k}`}
                  >
                    01.02.2026 · Örnek
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[9px] font-mono uppercase text-neutral-500 mb-0.5">Arka Plan</div>
                      <div className="flex items-center gap-1">
                        <input
                          type="color"
                          value={`#${bg}`}
                          onChange={(e) => setForm({ ...form, [c.k]: e.target.value.replace("#", "").toUpperCase() })}
                          className="w-8 h-8 border-2 border-[#09090B] cursor-pointer p-0 shrink-0"
                          data-testid={`color-picker-${c.k}`}
                          title={`${c.label} - arka plan`}
                        />
                        <Input
                          value={bg}
                          onChange={(e) => setForm({ ...form, [c.k]: e.target.value.replace(/^#/, "").toUpperCase() })}
                          maxLength={6}
                          className="h-8 rounded-none border-2 border-[#09090B] text-[11px] font-mono focus-visible:ring-0 flex-1 min-w-0"
                          data-testid={`color-hex-${c.k}`}
                          placeholder={c.def}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase text-neutral-500 mb-0.5">Yazı</div>
                      <div className="flex items-center gap-1">
                        <input
                          type="color"
                          value={`#${tx}`}
                          onChange={(e) => setForm({ ...form, [c.tk]: e.target.value.replace("#", "").toUpperCase() })}
                          className="w-8 h-8 border-2 border-[#09090B] cursor-pointer p-0 shrink-0"
                          data-testid={`text-color-picker-${c.k}`}
                          title={`${c.label} - yazı rengi`}
                        />
                        <Input
                          value={tx}
                          onChange={(e) => setForm({ ...form, [c.tk]: e.target.value.replace(/^#/, "").toUpperCase() })}
                          maxLength={6}
                          className="h-8 rounded-none border-2 border-[#09090B] text-[11px] font-mono focus-visible:ring-0 flex-1 min-w-0"
                          data-testid={`text-color-hex-${c.k}`}
                          placeholder={c.tdef}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t-2 border-dashed border-[#09090B] pt-4 space-y-3" data-testid="signature-template-xlsx-section">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Excel Şablonu</div>
          <div className="text-[11px] text-neutral-500 leading-relaxed">
            Şablonu indirip Excel'de düzenledikten sonra geri yükleyin. Kolon genişlikleri, satır yükseklikleri, font ve kenarlıklar aynen korunur. Placeholder'lar: <code className="font-mono">{"{{company_name}}"}</code>, <code className="font-mono">{"{{personnel_name}}"}</code>, <code className="font-mono">{"{{employee_no}}"}</code>, <code className="font-mono">{"{{period}}"}</code>, <code className="font-mono">{"{{logo}}"}</code>, <code className="font-mono">{"{{signature_block_1..N}}"}</code>, gün satırında <code className="font-mono">{"{{item.date}}"}</code>, <code className="font-mono">{"{{item.day}}"}</code>, <code className="font-mono">{"{{item.check_in}}"}</code>, <code className="font-mono">{"{{item.check_out}}"}</code>.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#09090B]"
              onClick={async () => {
                try {
                  const res = await api.get("/personnel/signature-timesheet/template.xlsx", { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = "imza_puantaji_sablonu.xlsx"; a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              }}
              data-testid="signature-template-download"
            >
              <Download size={14} className="mr-2" /> Şablonu İndir
            </Button>
            <label className="inline-flex items-center gap-2 border-2 border-[#09090B] px-3 py-2 cursor-pointer hover:bg-neutral-100 text-sm" data-testid="signature-template-upload">
              <Upload size={14} /> Şablonu Yükle
              <input
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  const fd = new FormData();
                  fd.append("file", f);
                  try {
                    await api.post("/personnel/signature-timesheet/template", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success("Şablon yüklendi");
                    await load();
                  } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                }}
              />
            </label>
            {form.signature_template_size ? (
              <div className="inline-flex items-center gap-2 border-2 border-emerald-700 px-3 py-1.5 bg-emerald-50 text-emerald-800 text-[11px] font-mono" data-testid="signature-template-status">
                Yüklü · {Math.round((form.signature_template_size || 0) / 1024)} KB
                <button
                  type="button"
                  className="text-red-700 hover:text-red-900"
                  onClick={async () => {
                    if (!window.confirm("Yüklü şablon kaldırılsın mı? Varsayılan şablon geri gelecek.")) return;
                    try {
                      await api.delete("/personnel/signature-timesheet/template");
                      toast.success("Şablon kaldırıldı");
                      await load();
                    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                  }}
                  data-testid="signature-template-clear"
                  title="Yüklü şablonu kaldır"
                >
                  <XIcon size={12} />
                </button>
              </div>
            ) : (
              <span className="text-[11px] text-neutral-500 italic">Henüz özel şablon yüklenmedi; varsayılan kullanılıyor</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
